/**
 * 직원 수정 가능 필드 업데이트 Hook (V40.2)
 *
 * updateEmployeeEditableFields callable 함수 호출 래퍼
 */

import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';

/**
 * V40.2: 직원 수정 가능 필드
 */
export interface UpdateEmployeeEditableFieldsData {
  // 연락처
  phoneNumber?: string;
  address?: string;

  // 역할
  roleIds?: string[];

  // 학력/기타
  educationLevel?: 'HIGH_SCHOOL' | 'COLLEGE_2Y' | 'COLLEGE_3Y' | 'UNIVERSITY' | 'GRADUATE';
  major?: string;
  educationYears?: number;
  certificatesText?: string;
  trainingsText?: string;
  commentText?: string;

  // V62: 소속 (회사/파견업체명)
  affiliationName?: string;
}

/**
 * 직원 수정 가능 필드 업데이트 Hook
 */
export function useEmployeeEditableFieldsMutations() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * 직원 수정 가능 필드 업데이트
   *
   * @param employeeId - 직원 ID
   * @param data - 수정할 필드 데이터
   * @returns 성공 여부
   */
  const updateEditableFields = async (
    employeeId: string,
    data: UpdateEmployeeEditableFieldsData
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      if (!functions) {
        throw new Error('Firebase Functions가 초기화되지 않았습니다.');
      }

      const updateEmployeeEditableFieldsFn = httpsCallable<
        { employeeId: string } & UpdateEmployeeEditableFieldsData,
        { success: boolean; message: string }
      >(functions, 'updateEmployeeEditableFields');

      const result = await updateEmployeeEditableFieldsFn({
        employeeId,
        ...data,
      });

      return true;
    } catch (err: any) {
      console.error('[useEmployeeEditableFieldsMutations] 수정 실패:', err);
      setError(err.message || '직원 정보 수정에 실패했습니다.');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    updateEditableFields,
    isLoading,
    error,
  };
}
